import React, { Component } from 'react'
import { Form, Formik } from 'formik'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium, media } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { H4, Text } from 'app/common/typography'
import { Error, Input } from 'app/common/forms'
import { object, string } from 'yup'
import { formatDate, isPastedDate, regex } from 'shared/utils'
import type { Kid } from 'shared/types'
import onClickOutside from 'react-onclickoutside'
import { ModalTitle } from 'app/common/modals/title'

const { red, yellow } = colors

type Props = {
    handleClose: () => void,
    kids: Array<Kid>,
    addKid: Kid => void,
    resetKidSuccess: () => void,
    addKidSuccess: boolean,
    addKidError: string
}
class KidsForm extends Component<Props> {
    /* Manage clickOutside Modal to close it */
    handleClickOutside(e) {
        const { handleClose } = this.props
        handleClose()
    }

    componentDidUpdate() {
        const { handleClose, addKidSuccess } = this.props
        if (addKidSuccess) {
            handleClose()
        }
    }

    componentWillUnmount() {
        const { resetKidSuccess } = this.props
        resetKidSuccess()
    }

    formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        return object().shape({
            firstname: string().required(mandatory),
            birthdate: string()
                .required(mandatory)
                .test('isValidDate', 'form.error.field.invalid', value => {
                    if (value) {
                        if (value.match(regex.DATE)) {
                            const date = value.split('/')
                            const dateFormatted = `${date[2]}-${date[1]}-${date[0]}`
                            return isPastedDate(dateFormatted)
                        }
                        if (value.match(regex.DATE_ISO)) {
                            return isPastedDate(value)
                        }
                    }
                    return false
                })
        })
    }

    render() {
        const {
            handleClose,
            kids,
            addKid,
            addKidError,
            resetKidSuccess
        } = this.props

        const initialValues = {
            firstname: '',
            birthdate: ''
        }

        return (
            <Content>
                <ModalTitle
                    center
                    handleClose={handleClose}
                    titleNode={
                        <FormattedMessage id="kingdom.profile.form.family.button.add">
                            {(text: string) => <StyledH4>{text}</StyledH4>}
                        </FormattedMessage>
                    }
                />
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={this.formSubscription}
                    onSubmit={async (values, actions) => {
                        resetKidSuccess()
                        const postedData = {
                            id: '',
                            firstname: values.firstname.trim(),
                            birthdate: formatDate(values.birthdate)
                        }
                        try {
                            await addKid(postedData)
                        } finally {
                            actions.setSubmitting(false)
                        }
                    }}>
                    {({
                        handleChange,
                        handleSubmit,
                        handleBlur,
                        values,
                        errors,
                        touched,
                        isSubmitting
                    }) => (
                        <StyledForm>
                            <FormattedMessage
                                id="kingdom.profile.form.family.child.label"
                                values={{ number: kids.length + 1 }}>
                                {(text: string) => <Text>{text}</Text>}
                            </FormattedMessage>
                            <Wrapped>
                                <Field>
                                    <Input
                                        name="firstname"
                                        type="text"
                                        labelKey="form.firstname.title"
                                        placeholderKey="form.firstname.placeholder"
                                        className={
                                            errors.firstname &&
                                            touched.firstname
                                                ? 'error'
                                                : ''
                                        }
                                    />
                                    {errors.firstname && touched.firstname && (
                                        <Error errorKey={errors.firstname} />
                                    )}
                                </Field>
                                <Field>
                                    <Input
                                        name="birthdate"
                                        type="date"
                                        labelKey="form.birthdate.title"
                                        placeholderKey="form.date.placeholder"
                                        className={`date ${
                                            errors.birthdate &&
                                            touched.birthdate
                                                ? 'error'
                                                : ''
                                        }`}
                                    />
                                    {errors.birthdate && touched.birthdate && (
                                        <Error errorKey={errors.birthdate} />
                                    )}
                                </Field>
                            </Wrapped>
                            <ButtonContainer>
                                <StyledButtonMedium
                                    onClick={handleClose}
                                    background="none"
                                    border={red}
                                    color={red}
                                    disabled={isSubmitting}>
                                    <FormattedMessage id="form.button.cancel" />
                                </StyledButtonMedium>
                                <StyledButtonMedium
                                    type="submit"
                                    background={yellow}
                                    border={yellow}
                                    disabled={isSubmitting}>
                                    <FormattedMessage id="kingdom.profile.form.button.add" />
                                </StyledButtonMedium>
                            </ButtonContainer>
                            {addKidError && (
                                <Mentions>
                                    <FormattedMessage id={addKidError}>
                                        {(text: string) => (
                                            <Text color={red}>{text}</Text>
                                        )}
                                    </FormattedMessage>
                                </Mentions>
                            )}
                        </StyledForm>
                    )}
                </Formik>
            </Content>
        )
    }
}

const Content = styled.div`
    padding: 0 20px;
    height: 100vh;
    ${media.medium`
        padding: 40px;
        height: auto;
    `};
`

const StyledH4 = styled(H4)`
    text-align: center;
`

const StyledForm = styled(Form)`
    padding-top: 20px;
    margin-top: 10px;
    border-top: 1px solid ${colors.lightGrey};
`

const Wrapped = styled.div`
    ${media.medium`
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    `};
`

const Field = styled.div`
    margin-bottom: 20px;
    ${media.medium`
        width: calc(50% - 10px);
    `};
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    margin: auto;
    justify-content: space-between;
    ${media.medium`
        justify-content: center;
    `};
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    width: calc(50% - 10px);
    ${media.medium`
        width: calc(50% - 20px);
        max-width: 200px;
        margin: 0 10px;
    `};
`

const Mentions = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
`

const KidsFormComponent = onClickOutside(KidsForm)

export { KidsFormComponent }
